import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Camera, ChevronRight, ChevronDown, MapPin, Circle, Droplets, Settings, Compass, Thermometer, Wind, CloudRain, Sun, Cloud, Ruler, Download, Check } from 'lucide-react';
import aiService from '../services/aiService';
import weatherService from '../services/weatherService';
import { compressImage, normalizeImage } from '../services/imageService';
import MapPicker from './MapPicker';
import CategoryInput from './CategoryInput';
import ArtVelger from './ArtVelger';

const WEATHER_CONDITIONS = [
  { name: "Opplett", icon: Sun },
  { name: "Regn", icon: CloudRain },
  { name: "Overskyet", icon: Cloud }
];

const WIND_DIRECTIONS = ["N", "NØ", "Ø", "SØ", "S", "SV", "V", "NV"];

// Lagre et bilde i kamerarullen.
// En web-app kan IKKE skrive direkte til Bilder-appen (sandboxing på iOS/Android).
// Det nærmeste er systemets delingsark der brukeren trykker "Lagre bilde".
// navigator.share MÅ kalles fra en bruker-gest (knappetrykk), derfor en knapp.
//
// VIKTIG: iOS Photos avviser bilder der filtype/-endelse ikke stemmer (HEIC,
// PNG med .jpg-navn osv.). Vi re-koder derfor til en ren JPEG via canvas først,
// slik at "Lagre bilde" alltid fungerer.
async function dataUrlToJpegFile(dataUrl) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  // Hvit bakgrunn i tilfelle kildebildet har gjennomsiktighet
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.95)
  );
  if (!blob) throw new Error('Kunne ikke konvertere bildet');

  return new File([blob], `fiskda-${Date.now()}.jpg`, { type: 'image/jpeg' });
}

export default function CaptureScreen({ onSave, onCancel, onFormChange, loading, initialData = null, mode = 'create', catches = [] }) {
  const isEdit = mode === 'edit';
  const [image, setImage] = useState(initialData?.image || null);
  // Spor om bildet er endret (nytt base64-bilde) i redigeringsmodus
  const [imageChanged, setImageChanged] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(initialData?.advanced && (initialData.advanced.length || initialData.advanced.circumference || initialData.advanced.width || initialData.advanced.gear || initialData.advanced.lure || initialData.advanced.notes))
  );
  const [showWeatherAdvanced, setShowWeatherAdvanced] = useState(!!initialData?.weather?.windDir);
  const [privacyMode, setPrivacyMode] = useState(initialData?.privacy || 'Ingen');
  const [posSelected, setPosSelected] = useState(!!initialData?.location);
  const [aiLoading, setAiLoading] = useState(false);
  const locationRef = useRef(initialData?.location || null); // Ref for location
  const [weatherAutoFilled, setWeatherAutoFilled] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [savedToRoll, setSavedToRoll] = useState(false);
  const [savingToRoll, setSavingToRoll] = useState(false);
  const cameraRollFileRef = useRef(null); // ferdig JPEG-fil, klar for deling

  const [data, setData] = useState({
    species: initialData?.species || '',
    latin: initialData?.latin || '',
    nickname: initialData?.nickname || '',
    weight: initialData?.weight != null ? String(initialData.weight) : '',
    waterType: initialData?.waterType || 'salt',
    caughtAt: initialData?.caughtAt || Date.now(),
    time: initialData?.time || '',
    weather: {
      temp: initialData?.weather?.temp != null ? String(initialData.weather.temp) : '',
      wind: initialData?.weather?.wind != null ? String(initialData.weather.wind) : '',
      condition: initialData?.weather?.condition || '',
      windDir: initialData?.weather?.windDir || '',
    },
    advanced: {
      length: initialData?.advanced?.length != null ? String(initialData.advanced.length) : '',
      circumference: initialData?.advanced?.circumference != null ? String(initialData.advanced.circumference) : '',
      width: initialData?.advanced?.width != null ? String(initialData.advanced.width) : '',
      gear: initialData?.advanced?.gear || '',
      lure: initialData?.advanced?.lure || '',
      tide: initialData?.advanced?.tide || '',
      notes: initialData?.advanced?.notes || ''
    },
    location: initialData?.location || null
  });

  // Unike verdier hentet fra eksisterende fangster — brukes som "kategorier"
  // i rullegardinene. Ingen egen lagring: kategoriene forsvinner selv når
  // ingen fangster lenger bruker dem, og nye dukker opp så snart de lagres.
  const uniqueValues = (list) => {
    const seen = new Map();
    for (const v of list) {
      const trimmed = (v || '').trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) seen.set(key, trimmed);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'no'));
  };

  const nicknameOptions = useMemo(
    () => uniqueValues(catches.map(c => c.nickname)),
    [catches]
  );
  const gearOptions = useMemo(
    () => uniqueValues(catches.map(c => c.advanced?.gear)),
    [catches]
  );
  const lureOptions = useMemo(
    () => uniqueValues(catches.map(c => c.advanced?.lure)),
    [catches]
  );

  // Handle privacy mode changes
  useEffect(() => {
    if (privacyMode === 'Ingen') {
      // Clear location when "Ingen" is selected
      setData(prev => ({ ...prev, location: null }));
      locationRef.current = null;
    }
  }, [privacyMode]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Normaliser bildet med en gang. Nyere iPhone (17 Pro) tar 48 MP-bilder
    // og kan levere HEIC — råbildet er for stort til å vises/tegnes på lerret
    // i iOS Safari og gir «hvit skjerm». normalizeImage skalerer ned og
    // re-koder til en trygg JPEG før vi viser eller lagrer bildet.
    try {
      const normalized = await normalizeImage(file);
      setImage(normalized);
      setImageChanged(true);
      setSavedToRoll(false); // nytt bilde — tilby lagring til kamerarull igjen
      cameraRollFileRef.current = null;

      // Forbered en ren JPEG-fil i bakgrunnen, så deling kan skje umiddelbart
      // ved knappetrykk (iOS krever at share() kalles i samme bruker-gest).
      dataUrlToJpegFile(normalized)
        .then((jpegFile) => { cameraRollFileRef.current = jpegFile; })
        .catch((err) => console.error('Kunne ikke forberede bilde:', err));

      // Kun kjør AI-identifikasjon hvis aktivert
      const aiEnabled = process.env.REACT_APP_ENABLE_AI === 'true';
      if (aiEnabled) {
        await identifyFish(normalized);
      }
    } catch (err) {
      console.error('Kunne ikke lese bildet:', err);
      alert('Kunne ikke lese bildet. Prøv et annet bilde, eller ta bildet på nytt.');
    }
  };

  // Lagre det tatte bildet i kamerarullen (via delingsark på mobil)
  const handleSaveToCameraRoll = async () => {
    if (!image) return;
    setSavingToRoll(true);
    try {
      // Bruk forhåndsbygd fil hvis klar, ellers bygg den nå
      const file = cameraRollFileRef.current || (await dataUrlToJpegFile(image));

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          setSavedToRoll(true);
        } catch (err) {
          if (err && err.name === 'AbortError') return; // bruker avbrøt
          throw err;
        }
      } else {
        // Desktop / uten fildeling: vanlig nedlasting
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        setSavedToRoll(true);
      }
    } catch (err) {
      console.error('Kunne ikke lagre bilde:', err);
      alert(
        'Kunne ikke lagre bildet automatisk. Trykk og hold på bildet over for å lagre det manuelt.'
      );
    } finally {
      setSavingToRoll(false);
    }
  };

  const identifyFish = async (base64Image) => {
    setAiLoading(true);
    try {
      const result = await aiService.identifyFish(base64Image);
      if (result && result.species && result.species !== 'Ukjent art') {
        setData(prev => ({ 
          ...prev, 
          species: result.species, 
          latin: result.latin 
        }));
      }
    } catch (error) {
      console.error('AI identifikasjon feilet:', error);
      // Vis feilmelding til bruker
      alert(`AI-identifikasjon feilet: ${error.message}\n\nVelg art manuelt fra listen.`);
    } finally {
      setAiLoading(false);
    }
  };

  const handleMapSelection = (location) => {
    // Lagre i ref for umiddelbar tilgang
    locationRef.current = {
      lat: location.lat,
      lng: location.lng
    };
    
    setPosSelected(true);
    setData(prev => {
      const newData = {
        ...prev,
        location: {
          lat: location.lat,
          lng: location.lng
        }
      };
      return newData;
    });
  };

  const fetchWeatherForLocation = async (lat, lng, timestamp) => {
    setWeatherLoading(true);
    try {
      const weather = await weatherService.fetchWeather(lat, lng, timestamp);
      if (weather) {
        setData(prev => ({
          ...prev,
          weather: {
            temp: weather.temp != null ? String(weather.temp) : prev.weather.temp,
            wind: weather.wind != null ? String(weather.wind) : prev.weather.wind,
            windDir: weather.windDir || prev.weather.windDir,
            condition: weather.condition || prev.weather.condition,
          }
        }));
        setWeatherAutoFilled(true);
      }
    } catch (err) {
      console.error('Auto-vær feilet:', err);
    } finally {
      setWeatherLoading(false);
    }
  };

  // Bygg riktig timestamp fra dato + klokkeslett
  const buildTimestamp = (caughtAt, time) => {
    if (!time) return caughtAt;
    const date = new Date(caughtAt);
    const parts = time.includes(':') ? time.split(':') : [time.slice(0, 2), time.slice(2)];
    date.setHours(parseInt(parts[0]) || 0, parseInt(parts[1]) || 0);
    return date.getTime();
  };

  // Hent vær automatisk når posisjon er satt
  // Re-hent hvis dato eller klokkeslett endres etter at posisjon er valgt
  // I redigeringsmodus: ikke overskriv lagret vær ved første lasting
  const prevWeatherKey = useRef(
    isEdit && initialData?.location
      ? `${initialData.location.lat},${initialData.location.lng},${buildTimestamp(initialData.caughtAt, initialData.time)}`
      : ''
  );
  useEffect(() => {
    if (!data.location) return;

    // Lag en nøkkel basert på posisjon + dato + tid for å unngå duplikate kall
    const timestamp = buildTimestamp(data.caughtAt, data.time);
    const key = `${data.location.lat},${data.location.lng},${timestamp}`;
    if (key === prevWeatherKey.current) return; // Allerede hentet for denne kombinasjonen
    prevWeatherKey.current = key;

    fetchWeatherForLocation(data.location.lat, data.location.lng, timestamp);
  }, [data.location, data.caughtAt, data.time]);

  // Sjekk om skjemaet har data som vil gå tapt
  const hasFormData = image || data.species || data.weight || data.time || data.location;

  // Rapporter til parent om skjemaet har data
  useEffect(() => {
    if (onFormChange) onFormChange(!!hasFormData);
  }, [hasFormData, onFormChange]);

  const handleSave = async () => {
    if (!image || !data.species) {
      alert('Legg til bilde og velg art før lagring');
      return;
    }

    // Komprimer bildet kun hvis det er et nytt base64-bilde.
    // I redigeringsmodus med uendret bilde beholder vi eksisterende Storage-URL.
    let imageToSave = image;
    if (image.startsWith('data:') && (imageChanged || !isEdit)) {
      imageToSave = await compressImage(image, 'storage');
    }

    const saveData = {
      ...data,
      image: imageToSave,
      privacy: privacyMode
    };

    // Bruk locationRef hvis state ikke er oppdatert ennå
    const locationToSave = data.location || locationRef.current;
    
    // Kun lagre location hvis den er valgt (ikke "Ingen")
    if (privacyMode !== 'Ingen' && locationToSave) {
      saveData.location = locationToSave;
    } else {
      saveData.location = null;
    }

    if (isEdit) {
      onSave(initialData.id, saveData);
    } else {
      console.log('Lagrer data:', saveData);
      onSave(saveData);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-40 animate-in fade-in duration-700">
      {!image ? (
        <div 
          onClick={() => document.getElementById('field-cam').click()}
          className="w-full aspect-[4/5] sm:aspect-video bg-white border-4 border-[#D1CCC0] border-double rounded-xl flex flex-col items-center justify-center gap-8 cursor-pointer shadow-inner"
        >
          <Camera size={72} strokeWidth={1} className="text-[#3B4D3C]" />
          <p className="font-serif italic text-2xl text-[#3B4D3C]">Ny Observasjon</p>
          <input 
            id="field-cam" 
            type="file" 
            accept="image/*" 
            onChange={handleFile} 
            className="hidden" 
          />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Bilde */}
          <div className="lg:w-1/2">
            <div className="sticky top-28 p-5 bg-white shadow-2xl border border-[#D1CCC0] transform -rotate-1">
              <img 
                src={image} 
                className="w-full aspect-[4/5] object-cover grayscale-[0.1]" 
                alt="Fangst" 
              />
              <div className="mt-6 pt-6 border-t border-[#D1CCC0]/40 text-center">
                <h3 className="font-serif text-4xl font-black italic text-[#3B4D3C] uppercase">
                  {data.species || "Velg art"}
                </h3>
                <p className="font-serif italic text-[#3B4D3C]/50 mt-1">{data.latin}</p>
              </div>

              {/* Lagre bildet i kamerarullen — kun for nytt tatt/valgt bilde */}
              {typeof image === 'string' && image.startsWith('data:') && (
                <button
                  type="button"
                  onClick={handleSaveToCameraRoll}
                  disabled={savingToRoll}
                  className={`mt-5 w-full py-3 rounded-md font-sans text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${
                    savedToRoll
                      ? 'bg-[#3B4D3C]/10 text-[#3B4D3C] border border-[#3B4D3C]/20'
                      : 'bg-[#3B4D3C] text-[#F8F5F0] hover:bg-[#2D3A2D] active:scale-95'
                  }`}
                >
                  {savingToRoll ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      Lagrer...
                    </>
                  ) : savedToRoll ? (
                    <>
                      <Check size={18} /> Lagret i kamerarull
                    </>
                  ) : (
                    <>
                      <Download size={18} /> Lagre bilde i kamerarull
                    </>
                  )}
                </button>
              )}
              <button 
                onClick={() => setImage(null)} 
                className="absolute -top-4 -right-4 w-10 h-10 bg-[#2D3436] text-white rounded-full flex items-center justify-center"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Skjema */}
          <div className="lg:w-1/2 space-y-8">
            <div className="bg-white p-8 border border-[#D1CCC0] shadow-sm space-y-8">
              {/* Vanntype */}
              <div className="flex gap-4">
                <button
                  onClick={() => setData({...data, waterType: 'salt'})}
                  className={`flex-1 py-4 rounded-md font-serif italic border ${
                    data.waterType === 'salt' 
                      ? 'bg-[#3B4D3C] text-white shadow-md' 
                      : 'bg-[#F8F5F0] text-[#3B4D3C]/50 border-[#D1CCC0]/30'
                  }`}
                >
                  Saltvann
                </button>
                <button
                  onClick={() => setData({...data, waterType: 'fresh'})}
                  className={`flex-1 py-4 rounded-md font-serif italic border ${
                    data.waterType === 'fresh' 
                      ? 'bg-[#3B4D3C] text-white shadow-md' 
                      : 'bg-[#F8F5F0] text-[#3B4D3C]/50 border-[#D1CCC0]/30'
                  }`}
                >
                  Ferskvann
                </button>
              </div>

              {/* Art */}
              <div 
                onClick={() => setShowPicker(true)} 
                className="cursor-pointer border-b-2 border-[#D1CCC0] py-4 flex justify-between items-center group"
              >
                <div>
                  <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block mb-1 italic">
                    Art
                  </label>
                  <p className="font-serif italic font-black text-3xl text-[#3B4D3C] group-hover:text-[#E67E22] transition-colors">
                    {data.species || "Velg Art"}
                  </p>
                </div>
                <ChevronRight size={28} className="text-[#D1CCC0]" />
              </div>

              {/* Kallenavn / fisketur-navn */}
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block italic">
                  Kallenavn / fisketur (valgfri)
                </label>
                <CategoryInput
                  value={data.nickname}
                  onChange={v => setData({ ...data, nickname: v })}
                  options={nicknameOptions}
                  maxLength={60}
                  placeholder="F.eks. Storfangsten på Sørlandet"
                  inputClassName="w-full bg-transparent font-serif italic text-xl border-b border-[#D1CCC0] outline-none py-1 placeholder:text-[#3B4D3C]/30"
                />
              </div>

              {/* Vekt og dato */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block italic">
                    Vekt (gram)
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    step="any"
                    value={data.weight} 
                    onChange={e => {
                      const v = e.target.value;
                      if (v === '' || parseFloat(v) >= 0) setData({...data, weight: v});
                    }}
                    className="w-full bg-transparent font-serif italic text-2xl border-b border-[#D1CCC0] outline-none py-1" 
                    placeholder="0" 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block italic">
                    Dato
                  </label>
                  <input 
                    type="date" 
                    value={new Date(data.caughtAt).toISOString().split('T')[0]} 
                    onChange={e => setData({...data, caughtAt: new Date(e.target.value).getTime()})}
                    className="w-full bg-transparent font-serif italic text-xl border-b border-[#D1CCC0] outline-none py-1" 
                  />
                </div>
              </div>

              {/* Klokkeslett */}
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block italic">
                  Klokkeslett (valgfri)
                </label>
                <input 
                  type="time" 
                  value={data.time} 
                  onFocus={e => {
                    if (!data.time) {
                      const now = new Date();
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');
                      setData({...data, time: `${hours}:${minutes}`});
                    }
                  }}
                  onChange={e => setData({...data, time: e.target.value})}
                  className="w-full bg-transparent font-serif italic text-xl border-b border-[#D1CCC0] outline-none py-1" 
                />
              </div>

              {/* Posisjon */}
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase text-[#3B4D3C]/40 tracking-widest block italic">
                  Fangststed
                </label>
                <div className="flex gap-2 mb-3">
                  <button 
                    onClick={() => setPrivacyMode('Ingen')}
                    className={`flex-1 py-2 text-[10px] font-bold uppercase rounded border transition-all ${
                      privacyMode === 'Ingen' 
                        ? 'bg-[#3B4D3C] text-white shadow-sm' 
                        : 'text-[#3B4D3C]/50 border-[#D1CCC0]'
                    }`}
                  >
                    Ingen
                  </button>
                  {["Nøyaktig", "100m", "1km"].map(m => (
                    <button 
                      key={m}
                      onClick={() => setPrivacyMode(m)}
                      className={`flex-1 py-2 text-[10px] font-bold uppercase rounded border transition-all ${
                        privacyMode === m 
                          ? 'bg-[#3B4D3C] text-white shadow-sm' 
                          : 'text-[#3B4D3C]/50 border-[#D1CCC0]'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {privacyMode !== 'Ingen' && (
                  <MapPicker 
                    privacyMode={privacyMode}
                    onLocationSelect={handleMapSelection}
                    initialLocation={data.location}
                  />
                )}
              </div>

              {/* Vær-observasjon */}
              <WeatherSection 
                data={data}
                setData={setData}
                showWeatherAdvanced={showWeatherAdvanced}
                setShowWeatherAdvanced={setShowWeatherAdvanced}
                weatherAutoFilled={weatherAutoFilled}
                weatherLoading={weatherLoading}
                hasLocation={!!data.location}
                hasTime={!!data.time}
              />

              {/* Avanserte mål */}
              <AdvancedSection 
                data={data}
                setData={setData}
                showAdvanced={showAdvanced}
                setShowAdvanced={setShowAdvanced}
                gearOptions={gearOptions}
                lureOptions={lureOptions}
              />

              {/* Lagre knapp */}
              <button 
                onClick={handleSave}
                disabled={loading || !image || !data.species}
                className="w-full bg-[#3B4D3C] text-[#F8F5F0] py-6 rounded shadow-xl font-serif font-black italic text-2xl hover:bg-[#2D3A2D] hover:scale-[1.01] active:scale-95 transition-all tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Lagrer...' : isEdit ? 'Lagre endringer' : 'Før inn i Håndboka'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Art_Velger — søk-og-liste for artsvalg (delt komponent, se ArtVelger.js) */}
      <ArtVelger
        open={showPicker}
        combineWaterTypes={false}
        onSelect={(art) => setData({ ...data, species: art.navn, latin: art.latin })}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
}

// Vær-seksjon
function WeatherSection({ data, setData, showWeatherAdvanced, setShowWeatherAdvanced, weatherAutoFilled, weatherLoading, hasLocation, hasTime }) {
  const showHint = !weatherAutoFilled && !weatherLoading && !hasLocation;

  return (
    <div className="p-5 bg-[#F8F5F0] border border-[#D1CCC0] rounded-lg space-y-4 shadow-inner">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#3B4D3C]">
          <Droplets size={16} />
          <span className="text-[10px] font-black uppercase tracking-widest italic text-[#3B4D3C]">
            Vær-observasjon
          </span>
        </div>
        {weatherLoading && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 border-2 border-[#E67E22] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-[8px] font-bold uppercase text-[#E67E22] tracking-widest">Henter vær...</span>
          </div>
        )}
        {!weatherLoading && weatherAutoFilled && (
          <span className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 tracking-widest italic">
            ✓ Hentet automatisk
          </span>
        )}
      </div>

      {showHint && (
        <div className="flex items-center gap-2 px-3 py-2 bg-white/60 border border-[#D1CCC0]/50 rounded-md">
          <Compass size={14} className="text-[#E67E22] shrink-0" />
          <p className="text-[10px] text-[#3B4D3C]/60 italic">
            Velg posisjon på kartet for automatisk værprognose
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 block tracking-widest">
            Temperatur °C
          </label>
          <input 
            type="number" 
            value={data.weather.temp} 
            placeholder="--" 
            onChange={e => setData({
              ...data, 
              weather: {...data.weather, temp: e.target.value}
            })}
            className="w-full bg-white text-center py-2 rounded-md font-serif italic text-lg shadow-sm border border-[#D1CCC0]/30" 
          />
        </div>
        <div className="space-y-1">
          <label className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 block tracking-widest">
            Vind m/s
          </label>
          <input 
            type="number" 
            min="0"
            step="any"
            value={data.weather.wind} 
            placeholder="--" 
            onChange={e => {
              const v = e.target.value;
              if (v === '' || parseFloat(v) >= 0) {
                setData({
                  ...data, 
                  weather: {...data.weather, wind: v}
                });
              }
            }}
            className="w-full bg-white text-center py-2 rounded-md font-serif italic text-lg shadow-sm border border-[#D1CCC0]/30" 
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 block tracking-widest">
          Forhold
        </label>
        <div className="flex flex-wrap gap-2">
          {WEATHER_CONDITIONS.map(c => {
            const IconComponent = c.icon;
            return (
              <button 
                key={c.name}
                onClick={() => setData({
                  ...data, 
                  weather: {...data.weather, condition: c.name}
                })}
                className={`px-4 py-2 rounded text-[10px] font-bold uppercase transition-all flex items-center gap-2 ${
                  data.weather.condition === c.name 
                    ? 'bg-[#3B4D3C] text-white shadow-md' 
                    : 'bg-white text-[#3B4D3C]/40 border border-[#D1CCC0]'
                }`}
              >
                <IconComponent size={12} />
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      <button 
        onClick={() => setShowWeatherAdvanced(!showWeatherAdvanced)}
        className="w-full py-2 text-[8px] font-black uppercase tracking-widest text-[#3B4D3C]/40 border-t border-[#D1CCC0] mt-2 flex items-center justify-center gap-2 hover:text-[#3B4D3C] transition-colors"
      >
        <Compass size={12} /> 
        {showWeatherAdvanced ? 'Skjul vindretning' : 'Legg til vindretning'}
      </button>

      {showWeatherAdvanced && (
        <div className="animate-in slide-in-from-top-1 space-y-3">
          <div className="space-y-1">
            <label className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 block tracking-widest">
              Vindretning
            </label>
            <div className="grid grid-cols-4 gap-2">
              {WIND_DIRECTIONS.map(dir => (
                <button
                  key={dir}
                  onClick={() => setData({
                    ...data, 
                    weather: {...data.weather, windDir: dir}
                  })}
                  className={`py-2 text-[10px] font-bold uppercase rounded transition-all ${
                    data.weather.windDir === dir 
                      ? 'bg-[#3B4D3C] text-white shadow-sm' 
                      : 'bg-white text-[#3B4D3C]/40 border border-[#D1CCC0]'
                  }`}
                >
                  {dir}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Avansert seksjon
function AdvancedSection({ data, setData, showAdvanced, setShowAdvanced, gearOptions, lureOptions }) {
  return (
    <div className="pt-4 border-t border-[#D1CCC0]">
      <button 
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="w-full flex items-center justify-between py-2 hover:bg-[#F8F5F0]/50 transition-colors"
      >
        <div className="flex items-center gap-2 text-[#3B4D3C]">
          <Settings size={18} />
          <span className="font-serif italic font-bold text-lg text-[#3B4D3C]">
            Mål & Utstyr
          </span>
        </div>
        <ChevronDown className={`transition-transform duration-300 ${showAdvanced ? 'rotate-180' : ''}`} />
      </button>

      {showAdvanced && (
        <div className="space-y-6 pt-6 animate-in slide-in-from-top-2">
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "length", label: "Lengde" },
              { key: "circumference", label: "Omkrets" }, 
              { key: "width", label: "Bredde" }
            ].map(field => (
              <div key={field.key} className="space-y-1">
                <label className="text-[8px] font-bold uppercase text-[#3B4D3C]/40 block tracking-widest">
                  {field.label} cm
                </label>
                <input 
                  type="number" 
                  min="0"
                  step="any"
                  placeholder="0" 
                  value={data.advanced[field.key]}
                  className="w-full bg-[#F8F5F0] text-center py-2 rounded-md font-serif italic outline-none border border-[#D1CCC0]/50 focus:border-[#3B4D3C]" 
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '' || parseFloat(v) >= 0) {
                      setData({
                        ...data, 
                        advanced: {...data.advanced, [field.key]: v}
                      });
                    }
                  }}
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CategoryInput
              value={data.advanced.gear}
              onChange={v => setData({ ...data, advanced: { ...data.advanced, gear: v } })}
              options={gearOptions}
              placeholder="Fiskeutstyr..."
              inputClassName="w-full bg-white border border-[#D1CCC0]/60 py-3 px-4 rounded-md font-serif italic text-sm outline-none focus:border-[#3B4D3C]"
            />
            <CategoryInput
              value={data.advanced.lure}
              onChange={v => setData({ ...data, advanced: { ...data.advanced, lure: v } })}
              options={lureOptions}
              placeholder="Sluk / Flue..."
              inputClassName="w-full bg-white border border-[#D1CCC0]/60 py-3 px-4 rounded-md font-serif italic text-sm outline-none focus:border-[#3B4D3C]"
            />
          </div>

          <textarea 
            rows="3" 
            placeholder="Notater fra turen..." 
            value={data.advanced.notes}
            className="w-full bg-white border border-[#D1CCC0]/60 py-3 px-4 rounded-md font-serif italic text-sm outline-none resize-none focus:border-[#3B4D3C]" 
            onChange={e => setData({
              ...data, 
              advanced: {...data.advanced, notes: e.target.value}
            })}
          />
        </div>
      )}
    </div>
  );
}